/**
 * Het belvenster (ontwerp v4 §9): nummer, recent, terugbellen, knop Bel.
 *
 * De knop Bel staat uit als er niets te bellen valt, mét de reden in
 * beeld. Nooit een knop die stil niets doet.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { toast } from "sonner";

import { isToetsWeigering } from "@/lib/toetsWeigering";
import { werkplekTegelApi, type BelGestart } from "@/lib/werkplek-api";

interface Props {
  zaakId?: string | null;
  klantId?: string | null;
  /** Een nummer dat al bekend is (Nieuw bericht naar een los nummer). */
  nummer?: string | null;
  /**
   * Waar de knop stond: "klantblok", "nieuw_bericht", "klantpagina" of
   * "tegel" (sessie 47, 9d). De klik maakt geen zaak; de bron komt op de
   * tijdlijn zodra Rinkel het gesprek meldt.
   */
  bron?: string;
  /** Zodra het gesprek loopt: de balk boven de tegel. */
  onGestart: (gesprek: BelGestart) => void;
  onSluiten?: () => void;
}

/** Terugval als de server geen eigen zin meestuurt (netwerkfout, 5xx). */
const BEL_FOUT_VAST =
  "Bellen is niet gelukt. Probeer het opnieuw of bel handmatig.";

/** Vaste toast-id: tien mislukte klikken geven één toast, niet tien. */
const BEL_FOUT_TOAST_ID = "bellen-fout";

/**
 * De zin bij een mislukte belpoging (sessie 20261023): de eigen zin van
 * de server uit `detail` (bij een 502 van Rinkel bijvoorbeeld "Het
 * gesprek kon niet gestart worden via Rinkel..."), anders de vaste zin.
 * Nooit de rauwe Axios-tekst ("Network Error").
 */
function belFoutTekst(fout: unknown): string {
  const detail = (fout as { response?: { data?: { detail?: unknown } } } | null)
    ?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  return BEL_FOUT_VAST;
}

/**
 * Meldt een mislukte belpoging ook met een toast, zodat de melding in
 * beeld komt als de regel bij de knop buiten beeld valt (iPhone). Een
 * toetsaccount-weigering kreeg al een eigen toast van de axios-laag.
 */
function meldBelFout(fout: unknown): void {
  if (isToetsWeigering(fout)) return;
  toast.error(belFoutTekst(fout), { id: BEL_FOUT_TOAST_ID });
}

function tijdstip(iso: string): string {
  return new Date(iso).toLocaleString("nl-NL", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function Belvenster({
  zaakId,
  klantId,
  nummer: bekendNummer,
  bron = "tegel",
  onGestart,
  onSluiten,
}: Props) {
  // Wat JP zelf heeft getypt. `null` = nog niets; dan wint het
  // voorstel uit het venster. Afgeleid in plaats van via een effect:
  // een setState in een effect geeft cascaderende renders, en een
  // gebruiker die net iets typte zou zijn invoer kunnen kwijtraken.
  const [getypt, setGetypt] = useState<string | null>(null);
  const [bezig, setBezig] = useState(false);

  const venster = useQuery({
    queryKey: ["werkplek", "belvenster", zaakId ?? null, klantId ?? null],
    queryFn: () => werkplekTegelApi.belvenster({ zaakId, klantId }),
  });

  const nummer = getypt ?? venster.data?.voorgesteld_nummer ?? bekendNummer ?? "";

  // Grendel tegen een dubbele tik: twee tikken in dezelfde render zien
  // allebei nog `isPending === false` en zouden twee keer bellen.
  const loopt = useRef(false);

  const bellen = useMutation({
    // Nooit stil opnieuw: een tweede POST kan dubbel bellen (recensie
    // 20261023), ook als de eerste na een time-out toch doorging.
    retry: false,
    onSettled: () => {
      loopt.current = false;
    },
    mutationFn: () =>
      werkplekTegelApi.bellen({
        nummer,
        zaak_id: zaakId ?? null,
        klant_id: klantId ?? venster.data?.klant_id ?? null,
        bron,
      }),
    onSuccess: (gesprek) => {
      setBezig(false);
      onGestart(gesprek);
    },
    // Eigen onError: de globale "Opslaan is niet gelukt" blijft weg.
    onError: (fout) => {
      setBezig(false);
      meldBelFout(fout);
    },
  });

  const kanBellen = nummer.trim().length >= 3;
  const redenUit = !kanBellen
    ? venster.data?.reden_uit ??
      "Vul een telefoonnummer in om te kunnen bellen."
    : null;

  // `bezig` gaat een tel later aan (zie start). Faalt het gesprek binnen
  // die tel, dan zou `bezig` daarna blijven hangen en de knop voorgoed
  // uit staan; daarom telt hij alleen zolang de aanvraag nog loopt.
  const knopUit = bezig && bellen.isPending;

  function start() {
    if (!kanBellen || loopt.current) return;
    loopt.current = true;
    bellen.mutate();
    // Pas een tel later uitzetten: `disabled` in dezelfde tel annuleert
    // de handeling eronder (les 17-09).
    window.setTimeout(() => setBezig(true), 0);
  }

  return (
    <div className="wp-belvenster" role="dialog" aria-label="Bellen">
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <strong style={{ fontSize: 13 }}>Bellen</strong>
        {onSluiten ? (
          <button
            type="button"
            className="wp-knop wp-tegel-knop-klein"
            style={{ marginLeft: "auto" }}
            onClick={onSluiten}
          >
            Sluiten
          </button>
        ) : null}
      </div>

      <label style={{ fontSize: 12, color: "var(--wp-tekst2)" }}>
        Telefoonnummer
        <input
          type="tel"
          value={nummer}
          onChange={(e) => setGetypt(e.target.value)}
          placeholder="06 12 34 56 78"
          autoComplete="tel"
        />
      </label>

      {venster.data?.klant_naam ? (
        <div style={{ fontSize: 12.5, color: "var(--wp-tekst2)" }}>
          Klantkaart: <strong>{venster.data.klant_naam}</strong>
        </div>
      ) : null}

      {venster.data && venster.data.terugbellen.length > 0 ? (
        <div>
          <div style={{ fontSize: 11, color: "var(--wp-tekst3)" }}>
            Terugbellen
          </div>
          <ul className="wp-bellijst">
            {venster.data.terugbellen.map((regel) => (
              <li key={regel.id}>
                <button
                  type="button"
                  onClick={() => setGetypt(regel.nummer ?? "")}
                >
                  {regel.klant_naam ?? regel.nummer ?? "onbekend"} ·{" "}
                  {tijdstip(regel.moment)}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {venster.data && venster.data.recent.length > 0 ? (
        <div>
          <div style={{ fontSize: 11, color: "var(--wp-tekst3)" }}>Recent</div>
          <ul className="wp-bellijst">
            {venster.data.recent.map((regel) => (
              <li key={regel.id}>
                <button
                  type="button"
                  onClick={() => setGetypt(regel.nummer ?? "")}
                >
                  {regel.klant_naam ?? regel.nummer ?? "onbekend"} ·{" "}
                  {tijdstip(regel.moment)}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {bellen.isError ? (
        <p className="wp-tegel-fout" role="alert">
          {belFoutTekst(bellen.error)}
        </p>
      ) : null}

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button
          type="button"
          className="wp-knop wp-knop-primair"
          onClick={start}
          disabled={!kanBellen || knopUit}
        >
          Bel
        </button>
        {redenUit ? (
          <span style={{ fontSize: 11, color: "var(--wp-tekst3)" }}>
            {redenUit}
          </span>
        ) : null}
      </div>
    </div>
  );
}
